/**
 * نظام التخزين المؤقت والتحميل المسبق الفوري للبيانات (In-Memory SWR Data Cache).
 * يحاكي تجربة تطبيقات iOS وتيليجرام:
 * - البيانات تُعرض فورًا من الذاكرة في 0ms دون إظهار شاشات أو سبينرات "جارٍ التحميل".
 * - التحديث يتم بسلاسة في الخلفية (Stale-While-Revalidate).
 * - التحميل المسبق التلقائي يملأ الذاكرة قبل أن يضغط المستخدم على أي زر أو تبويبة.
 */

import type {
  Task,
  TaskTemplate,
  Brand,
  Branch,
  AppNotification,
  TaskAssignee,
  TaskCategory,
  Role,
  Membership,
  SupervisionRelation,
} from '@/domain/types';
import { listTasks, taskCounts, type TaskView } from '@/services/task-service';
import { listManualItems, listStaffRoles, type ManualItemRecord, type StaffRoleRecord } from '@/services/store-manual-service';
import { listSpreadsheets, type SpreadsheetDocument } from '@/services/spreadsheet-service';
import { listTemplates } from '@/services/template-service';
import {
  listBrands,
  listBranches,
  listCategories,
  listMembershipDirectory,
  listRoles,
  listSupervision,
  type MembershipDirectoryEntry,
} from '@/services/org-service';
import {
  listBulletins,
  listShiftHandovers,
  listMediaHubItems,
  type BulletinRecord,
  type ShiftHandoverRecord,
  type MediaHubItem,
} from '@/services/operations-hub-service';
import { listMyNotifications } from '@/services/notification-service';
import { fetchReportTasks, summarizeReport, type ReportSummary } from '@/services/report-service';
import { todayInZone, addDaysLocal } from '@/lib/format';

interface CacheRecord<T> {
  data: T;
  timestamp: number;
}

export interface DashboardCachedData {
  tasks: Task[];
  counts: {
    assigned: number;
    created: number;
    overdue: number;
    pendingApproval: number;
    personal: number;
    completed: number;
  };
  approvals: Task[];
}

export interface TasksListCachedData {
  rows: Task[];
  total: number | null;
}

export interface StoreManualCachedData {
  brands: Brand[];
  manualItems: ManualItemRecord[];
  spreadsheets: SpreadsheetDocument[];
}

export interface StaffRolesCachedData {
  brands: Brand[];
  staffRoles: StaffRoleRecord[];
}

export interface BulletinsCachedData {
  bulletins: BulletinRecord[];
  branches: Branch[];
}

export interface ShiftHandoverCachedData {
  handovers: ShiftHandoverRecord[];
  branches: Branch[];
}

export interface NotificationsCachedData {
  notifications: AppNotification[];
}

export interface ReportsCachedData {
  tasks: Task[];
  assignees: TaskAssignee[];
  summary: ReportSummary;
}

export interface MediaHubCachedData {
  items: MediaHubItem[];
  branches: Branch[];
}

export interface CategoriesCachedData {
  categories: TaskCategory[];
}

export interface UsersDirectoryCachedData {
  directory: MembershipDirectoryEntry[];
  roles: Role[];
}

export interface RolesCachedData {
  roles: Role[];
}

export interface SupervisionCachedData {
  relations: SupervisionRelation[];
  roles: Role[];
  members: Membership[];
  memberRoleIds: Map<string, string[]>;
}

const memoryCache = new Map<string, CacheRecord<any>>();
const inFlightPromises = new Map<string, Promise<any>>();
const CACHE_STORAGE_PREFIX = 'taskflow.cache.v2.';
const LEGACY_STORAGE_PREFIX = 'taskflow.cache.';

function getPersistentStorage(): Storage | null {
  try {
    if (typeof localStorage !== 'undefined') return localStorage;
  } catch {}
  try {
    if (typeof sessionStorage !== 'undefined') return sessionStorage;
  } catch {}
  return null;
}

export function getCachedData<T>(key: string): T | undefined {
  const record = memoryCache.get(key);
  if (record) return record.data as T;
  try {
    const storage = getPersistentStorage();
    if (storage) {
      let raw = storage.getItem(`${CACHE_STORAGE_PREFIX}${key}`);
      if (!raw) {
        raw = storage.getItem(`${LEGACY_STORAGE_PREFIX}${key}`);
      }
      if (raw) {
        const parsed = JSON.parse(raw) as CacheRecord<T>;
        memoryCache.set(key, parsed);
        return parsed.data;
      }
    }
  } catch {}
  return undefined;
}

export function setCachedData<T>(key: string, data: T): void {
  const record: CacheRecord<T> = { data, timestamp: Date.now() };
  memoryCache.set(key, record);
  try {
    const storage = getPersistentStorage();
    if (storage) {
      storage.setItem(`${CACHE_STORAGE_PREFIX}${key}`, JSON.stringify(record));
    }
  } catch {
    // في حال امتلاء مساحة التخزين، ننظف المفاتيح القديمة ونحفظ مجددًا
    try {
      const storage = getPersistentStorage();
      if (storage) {
        const toRemove: string[] = [];
        for (let i = 0; i < storage.length; i++) {
          const k = storage.key(i);
          if (k && (k.startsWith(CACHE_STORAGE_PREFIX) || k.startsWith(LEGACY_STORAGE_PREFIX))) {
            toRemove.push(k);
          }
        }
        for (const k of toRemove.slice(0, Math.max(1, Math.floor(toRemove.length / 2)))) {
          storage.removeItem(k);
        }
        storage.setItem(`${CACHE_STORAGE_PREFIX}${key}`, JSON.stringify(record));
      }
    } catch {}
  }
}

export function hasCachedData(key: string): boolean {
  if (memoryCache.has(key)) return true;
  try {
    const storage = getPersistentStorage();
    if (storage) {
      return (
        storage.getItem(`${CACHE_STORAGE_PREFIX}${key}`) !== null ||
        storage.getItem(`${LEGACY_STORAGE_PREFIX}${key}`) !== null
      );
    }
  } catch {}
  return false;
}

export function getCacheAge(key: string): number {
  const record = memoryCache.get(key);
  if (record) return Date.now() - record.timestamp;
  try {
    const storage = getPersistentStorage();
    if (storage) {
      const raw =
        storage.getItem(`${CACHE_STORAGE_PREFIX}${key}`) ||
        storage.getItem(`${LEGACY_STORAGE_PREFIX}${key}`);
      if (raw) {
        const parsed = JSON.parse(raw) as CacheRecord<any>;
        return Date.now() - (parsed.timestamp || 0);
      }
    }
  } catch {}
  return Infinity;
}

export function invalidateDataCache(keyOrPrefix?: string): void {
  const storages: Storage[] = [];
  try { if (typeof localStorage !== 'undefined') storages.push(localStorage); } catch {}
  try { if (typeof sessionStorage !== 'undefined') storages.push(sessionStorage); } catch {}

  if (!keyOrPrefix) {
    memoryCache.clear();
    for (const storage of storages) {
      try {
        const toRemove: string[] = [];
        for (let i = 0; i < storage.length; i++) {
          const k = storage.key(i);
          if (k && (k.startsWith(CACHE_STORAGE_PREFIX) || k.startsWith(LEGACY_STORAGE_PREFIX))) {
            toRemove.push(k);
          }
        }
        for (const k of toRemove) storage.removeItem(k);
      } catch {}
    }
    return;
  }

  for (const k of Array.from(memoryCache.keys())) {
    if (k === keyOrPrefix || k.startsWith(keyOrPrefix)) {
      memoryCache.delete(k);
    }
  }
  for (const storage of storages) {
    try {
      const targetV2 = `${CACHE_STORAGE_PREFIX}${keyOrPrefix}`;
      const targetLegacy = `${LEGACY_STORAGE_PREFIX}${keyOrPrefix}`;
      const toRemove: string[] = [];
      for (let i = 0; i < storage.length; i++) {
        const k = storage.key(i);
        if (k && (k === targetV2 || k.startsWith(targetV2) || k === targetLegacy || k.startsWith(targetLegacy))) {
          toRemove.push(k);
        }
      }
      for (const k of toRemove) storage.removeItem(k);
    } catch {}
  }
}

/**
 * تحديث تفاؤلي فوري لمهمة في الذاكرة دون انتظار طلب الشبكة (Optimistic Cache Update)
 */
export function optimisticUpdateTaskInCache(taskId: string, updater: (t: Task) => Task): void {
  for (const [key, record] of Array.from(memoryCache.entries())) {
    if (key.startsWith('dashboard:')) {
      const data = record.data as DashboardCachedData;
      if (data && Array.isArray(data.tasks)) {
        let changed = false;
        const newTasks = data.tasks.map((task) => {
          if (task.id === taskId) {
            changed = true;
            return updater(task);
          }
          return task;
        });
        if (changed) {
          setCachedData(key, { ...data, tasks: newTasks });
        }
      }
    } else if (key.startsWith('tasks-list:')) {
      const data = record.data as TasksListCachedData;
      if (data && Array.isArray(data.rows)) {
        let changed = false;
        const newRows = data.rows.map((row) => {
          if (row.id === taskId) {
            changed = true;
            return updater(row);
          }
          return row;
        });
        if (changed) {
          setCachedData(key, { ...data, rows: newRows });
        }
      }
    }
  }
}

/**
 * إزالة فورية لمهمة من الذاكرة المحلية (Optimistic Cache Deletion)
 */
export function optimisticRemoveTaskFromCache(taskId: string): void {
  for (const [key, record] of Array.from(memoryCache.entries())) {
    if (key.startsWith('dashboard:')) {
      const data = record.data as DashboardCachedData;
      if (data && Array.isArray(data.tasks)) {
        setCachedData(key, {
          ...data,
          tasks: data.tasks.filter((t) => t.id !== taskId),
          approvals: Array.isArray(data.approvals) ? data.approvals.filter((t) => t.id !== taskId) : [],
        });
      }
    } else if (key.startsWith('tasks-list:')) {
      const data = record.data as TasksListCachedData;
      if (data && Array.isArray(data.rows)) {
        setCachedData(key, {
          ...data,
          rows: data.rows.filter((r) => r.id !== taskId),
          total: typeof data.total === 'number' ? Math.max(0, data.total - 1) : data.total,
        });
      }
    }
  }
}

export function getDashboardCacheKey(companyId: string, membershipId: string | null = '', branchId = ''): string {
  return `dashboard:${companyId}:${membershipId || ''}:${branchId || ''}`;
}

// ---------------------------------------------------------------------------
// Preloaders
// ---------------------------------------------------------------------------

export async function preloadDashboardData(
  gateway: any,
  actor: { companyId: string; membershipId: string | null; userId: string; isPlatformSupport?: boolean },
  timeZone = 'Asia/Riyadh',
  supervisedMembershipIds: string[] = [],
  branchId = '',
): Promise<DashboardCachedData | undefined> {
  if (!actor?.companyId) return undefined;
  const cacheKey = getDashboardCacheKey(actor.companyId, actor.membershipId, branchId);
  if (inFlightPromises.has(cacheKey)) return inFlightPromises.get(cacheKey);

  const promise = (async () => {
    try {
      const today = todayInZone(timeZone);
      const base = {
        companyId: actor.companyId,
        actorMembershipId: actor.membershipId,
        isPlatformSupport: actor.isPlatformSupport,
        supervisedMembershipIds,
      };
      const [activeTasks, completedTasks, pendingApprovalRows, assignedTasks] = await Promise.all([
        listTasks(gateway, {
          ...base,
          view: 'board',
          filters: {
            status: ['scheduled', 'pending', 'in_progress'],
            pageSize: 200,
          },
        }),
        listTasks(gateway, {
          ...base,
          view: 'board',
          filters: {
            status: ['completed'],
            from: today,
            pageSize: 50,
          },
        }),
        gateway.table('tasks').list({
          filter: {
            companyId: { op: 'eq', value: actor.companyId },
            approvalState: { op: 'eq', value: 'pending' },
            deletedAt: { op: 'isNull' },
          },
          orderBy: [{ field: 'updatedAt', direction: 'desc' }],
          limit: 8,
        }),
        listTasks(gateway, { ...base, view: 'assigned', filters: { pageSize: 1 } }),
      ]);

      const nextCounts = await taskCounts(gateway, {
        companyId: actor.companyId,
        membershipId: actor.membershipId,
        isPlatformSupport: actor.isPlatformSupport,
        userId: actor.userId,
        supervisedMembershipIds,
        activeTasks: activeTasks.rows,
        assignedTasks,
        completedCount: completedTasks.total ?? completedTasks.rows.length,
      });

      const data: DashboardCachedData = {
        tasks: [...activeTasks.rows, ...completedTasks.rows],
        counts: nextCounts,
        approvals: pendingApprovalRows,
      };
      setCachedData(cacheKey, data);
      return data;
    } catch {
      return undefined;
    } finally {
      inFlightPromises.delete(cacheKey);
    }
  })();

  inFlightPromises.set(cacheKey, promise);
  return promise;
}

export async function preloadTasksListView(
  gateway: any,
  actor: { companyId: string; membershipId: string | null; userId: string; isPlatformSupport?: boolean },
  view: TaskView,
  supervisedMembershipIds: string[] = [],
  page = 1,
  pageSize = 25,
): Promise<TasksListCachedData | undefined> {
  if (!actor?.companyId) return undefined;
  const cacheKey = `tasks-list:${actor.companyId}:${actor.membershipId || ''}:${view}:${page}`;
  if (inFlightPromises.has(cacheKey)) return inFlightPromises.get(cacheKey);

  const promise = (async () => {
    try {
      const result = await listTasks(gateway, {
        companyId: actor.companyId,
        view,
        actorMembershipId: actor.membershipId,
        isPlatformSupport: actor.isPlatformSupport,
        supervisedMembershipIds,
        filters: { page, pageSize },
      });
      const data: TasksListCachedData = { rows: result.rows, total: result.total };
      setCachedData(cacheKey, data);
      return data;
    } catch {
      return undefined;
    } finally {
      inFlightPromises.delete(cacheKey);
    }
  })();

  inFlightPromises.set(cacheKey, promise);
  return promise;
}

export async function preloadStoreManualData(
  gateway: any,
  companyId: string,
): Promise<StoreManualCachedData | undefined> {
  if (!companyId) return undefined;
  const cacheKey = `store-manual:${companyId}`;
  if (inFlightPromises.has(cacheKey)) return inFlightPromises.get(cacheKey);

  const promise = (async () => {
    try {
      const [brandList, items, sheets] = await Promise.all([
        listBrands(gateway, companyId).catch(() => [] as Brand[]),
        listManualItems(gateway, companyId).catch(() => [] as ManualItemRecord[]),
        listSpreadsheets(gateway, companyId).catch(() => [] as SpreadsheetDocument[]),
      ]);
      const data: StoreManualCachedData = {
        brands: brandList,
        manualItems: items,
        spreadsheets: sheets,
      };
      setCachedData(cacheKey, data);
      return data;
    } catch {
      return undefined;
    } finally {
      inFlightPromises.delete(cacheKey);
    }
  })();

  inFlightPromises.set(cacheKey, promise);
  return promise;
}

export async function preloadStaffRolesData(
  gateway: any,
  companyId: string,
): Promise<StaffRolesCachedData | undefined> {
  if (!companyId) return undefined;
  const cacheKey = `staff-roles:${companyId}`;
  if (inFlightPromises.has(cacheKey)) return inFlightPromises.get(cacheKey);

  const promise = (async () => {
    try {
      const [brandList, roles] = await Promise.all([
        listBrands(gateway, companyId).catch(() => [] as Brand[]),
        listStaffRoles(gateway, companyId).catch(() => [] as StaffRoleRecord[]),
      ]);
      const data: StaffRolesCachedData = {
        brands: brandList,
        staffRoles: roles,
      };
      setCachedData(cacheKey, data);
      return data;
    } catch {
      return undefined;
    } finally {
      inFlightPromises.delete(cacheKey);
    }
  })();

  inFlightPromises.set(cacheKey, promise);
  return promise;
}

export async function preloadTemplatesData(
  gateway: any,
  companyId: string,
  includeArchived = false,
): Promise<TaskTemplate[] | undefined> {
  if (!companyId) return undefined;
  const cacheKey = `templates:${companyId}:${includeArchived}`;
  if (inFlightPromises.has(cacheKey)) return inFlightPromises.get(cacheKey);

  const promise = (async () => {
    try {
      const rows = await listTemplates(gateway, companyId, includeArchived).catch(() => []);
      setCachedData(cacheKey, rows);
      return rows;
    } catch {
      return undefined;
    } finally {
      inFlightPromises.delete(cacheKey);
    }
  })();

  inFlightPromises.set(cacheKey, promise);
  return promise;
}

export async function preloadBulletinsData(
  gateway: any,
  companyId: string,
): Promise<BulletinsCachedData | undefined> {
  if (!companyId) return undefined;
  const cacheKey = `bulletins:${companyId}`;
  if (inFlightPromises.has(cacheKey)) return inFlightPromises.get(cacheKey);

  const promise = (async () => {
    try {
      const [bulletinRows, branchRows] = await Promise.all([
        listBulletins(gateway, companyId),
        listBranches(gateway, companyId).catch(() => [] as Branch[]),
      ]);
      const data: BulletinsCachedData = {
        bulletins: bulletinRows,
        branches: branchRows.filter((b) => !b.deletedAt && b.status === 'active'),
      };
      setCachedData(cacheKey, data);
      return data;
    } catch {
      return undefined;
    } finally {
      inFlightPromises.delete(cacheKey);
    }
  })();

  inFlightPromises.set(cacheKey, promise);
  return promise;
}

export async function preloadShiftHandoversData(
  gateway: any,
  companyId: string,
): Promise<ShiftHandoverCachedData | undefined> {
  if (!companyId) return undefined;
  const cacheKey = `shift-handovers:${companyId}`;
  if (inFlightPromises.has(cacheKey)) return inFlightPromises.get(cacheKey);

  const promise = (async () => {
    try {
      const [handoverRows, branchRows] = await Promise.all([
        listShiftHandovers(gateway, companyId),
        listBranches(gateway, companyId).catch(() => [] as Branch[]),
      ]);
      const data: ShiftHandoverCachedData = {
        handovers: handoverRows,
        branches: branchRows.filter((b) => !b.deletedAt),
      };
      setCachedData(cacheKey, data);
      return data;
    } catch {
      return undefined;
    } finally {
      inFlightPromises.delete(cacheKey);
    }
  })();

  inFlightPromises.set(cacheKey, promise);
  return promise;
}

export async function preloadNotificationsData(
  gateway: any,
  companyId: string,
  limit = 200,
): Promise<NotificationsCachedData | undefined> {
  if (!companyId) return undefined;
  const cacheKey = `notifications:${companyId}`;
  if (inFlightPromises.has(cacheKey)) return inFlightPromises.get(cacheKey);

  const promise = (async () => {
    try {
      const items = await listMyNotifications(gateway, companyId, limit);
      const data: NotificationsCachedData = { notifications: items };
      setCachedData(cacheKey, data);
      return data;
    } catch {
      return undefined;
    } finally {
      inFlightPromises.delete(cacheKey);
    }
  })();

  inFlightPromises.set(cacheKey, promise);
  return promise;
}

export async function preloadReportsData(
  gateway: any,
  companyId: string,
  timeZone = 'Asia/Riyadh',
  memberNames: Map<string, string> = new Map(),
): Promise<ReportsCachedData | undefined> {
  if (!companyId) return undefined;
  const cacheKey = `reports:${companyId}`;
  if (inFlightPromises.has(cacheKey)) return inFlightPromises.get(cacheKey);

  const promise = (async () => {
    try {
      const today = todayInZone(timeZone);
      const filters = {
        companyId,
        from: addDaysLocal(today, -29),
        to: today,
        branchId: null,
        brandId: null,
        assigneeMembershipId: null,
      };
      const data = await fetchReportTasks(gateway, filters);
      const summary = summarizeReport(data.tasks, data.assignees, memberNames);
      const cached: ReportsCachedData = {
        tasks: data.tasks,
        assignees: data.assignees,
        summary,
      };
      setCachedData(cacheKey, cached);
      return cached;
    } catch {
      return undefined;
    } finally {
      inFlightPromises.delete(cacheKey);
    }
  })();

  inFlightPromises.set(cacheKey, promise);
  return promise;
}

export async function preloadMediaHubData(
  gateway: any,
  companyId: string,
): Promise<MediaHubCachedData | undefined> {
  if (!companyId) return undefined;
  const cacheKey = `media-hub:${companyId}`;
  if (inFlightPromises.has(cacheKey)) return inFlightPromises.get(cacheKey);

  const promise = (async () => {
    try {
      const [hubItems, branchRows] = await Promise.all([
        listMediaHubItems(gateway, companyId),
        listBranches(gateway, companyId).catch(() => [] as Branch[]),
      ]);
      const data: MediaHubCachedData = {
        items: hubItems,
        branches: branchRows.filter((b) => !b.deletedAt),
      };
      setCachedData(cacheKey, data);
      return data;
    } catch {
      return undefined;
    } finally {
      inFlightPromises.delete(cacheKey);
    }
  })();

  inFlightPromises.set(cacheKey, promise);
  return promise;
}

export async function preloadCategoriesData(
  gateway: any,
  companyId: string,
): Promise<CategoriesCachedData | undefined> {
  if (!companyId) return undefined;
  const cacheKey = `categories:${companyId}`;
  if (inFlightPromises.has(cacheKey)) return inFlightPromises.get(cacheKey);

  const promise = (async () => {
    try {
      const rows = await listCategories(gateway, companyId);
      const data: CategoriesCachedData = { categories: rows };
      setCachedData(cacheKey, data);
      return data;
    } catch {
      return undefined;
    } finally {
      inFlightPromises.delete(cacheKey);
    }
  })();

  inFlightPromises.set(cacheKey, promise);
  return promise;
}

export async function preloadUsersDirectoryData(
  gateway: any,
  companyId: string,
): Promise<UsersDirectoryCachedData | undefined> {
  if (!companyId) return undefined;
  const cacheKey = `users-directory:${companyId}`;
  if (inFlightPromises.has(cacheKey)) return inFlightPromises.get(cacheKey);

  const promise = (async () => {
    try {
      const [directory, nextRoles] = await Promise.all([
        listMembershipDirectory(gateway, companyId),
        listRoles(gateway, companyId),
      ]);
      const data: UsersDirectoryCachedData = { directory, roles: nextRoles };
      setCachedData(cacheKey, data);
      return data;
    } catch {
      return undefined;
    } finally {
      inFlightPromises.delete(cacheKey);
    }
  })();

  inFlightPromises.set(cacheKey, promise);
  return promise;
}

export async function preloadRolesData(
  gateway: any,
  companyId: string,
): Promise<RolesCachedData | undefined> {
  if (!companyId) return undefined;
  const cacheKey = `roles:${companyId}`;
  if (inFlightPromises.has(cacheKey)) return inFlightPromises.get(cacheKey);

  const promise = (async () => {
    try {
      const nextRoles = await listRoles(gateway, companyId);
      const data: RolesCachedData = { roles: nextRoles };
      setCachedData(cacheKey, data);
      return data;
    } catch {
      return undefined;
    } finally {
      inFlightPromises.delete(cacheKey);
    }
  })();

  inFlightPromises.set(cacheKey, promise);
  return promise;
}

export async function preloadSupervisionData(
  gateway: any,
  companyId: string,
): Promise<SupervisionCachedData | undefined> {
  if (!companyId) return undefined;
  const cacheKey = `supervision:${companyId}`;
  if (inFlightPromises.has(cacheKey)) return inFlightPromises.get(cacheKey);

  const promise = (async () => {
    try {
      const [nextRelations, nextRoles, directory] = await Promise.all([
        listSupervision(gateway, companyId),
        listRoles(gateway, companyId),
        listMembershipDirectory(gateway, companyId),
      ]);
      const roleAssignments = directory.length
        ? await gateway.table('membership_roles').list({
            filter: { membershipId: { op: 'in', value: directory.map((entry) => entry.membership.id) } },
          })
        : [];
      const now = Date.now();
      const activeRoleAssignments = roleAssignments.filter(
        (assignment: any) => !assignment.expiresAt || Date.parse(assignment.expiresAt) > now,
      );
      const memberRoleIds = new Map<string, string[]>(
        directory.map((entry) => [
          entry.membership.id,
          activeRoleAssignments
            .filter((assignment: any) => assignment.membershipId === entry.membership.id)
            .map((entry: any) => entry.roleId),
        ]),
      );
      const data: SupervisionCachedData = {
        relations: nextRelations,
        roles: nextRoles,
        members: directory.map((entry) => entry.membership),
        memberRoleIds,
      };
      setCachedData(cacheKey, data);
      return data;
    } catch {
      return undefined;
    } finally {
      inFlightPromises.delete(cacheKey);
    }
  })();

  inFlightPromises.set(cacheKey, promise);
  return promise;
}

function runWhenIdle(callback: () => void, timeout = 250): void {
  if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
    (window as any).requestIdleCallback(callback, { timeout });
  } else {
    setTimeout(callback, Math.min(timeout, 80));
  }
}

let lastPreloadedCompanyId = '';

/**
 * دالة التحميل المسبق الشاملة — تشغل في خلفية المتصفح أثناء أوقات الخمول بدون التأثير على سلاسة الواجهة إطلاقًا
 */
export function preloadAllAppData(
  gateway: any,
  actor: any,
  settings: any,
  access: any,
  lookups?: any,
) {
  if (!actor?.companyId) return;
  if (lastPreloadedCompanyId === actor.companyId) return;
  lastPreloadedCompanyId = actor.companyId;

  const timeZone = settings?.timeZone ?? 'Asia/Riyadh';
  const supervisedIds = access?.supervisedMembershipIds ?? [];

  // المرحلة الأولى: لوحة المهام والمهام المسندة الأساسية
  void preloadDashboardData(gateway, actor, timeZone, supervisedIds);
  void preloadTasksListView(gateway, actor, 'assigned', supervisedIds);

  // المرحلة الثانية (أثناء خمول المتصفح): الدليل التشغيلي، أدوار الموظفين، الإشعارات، والتعاميم
  runWhenIdle(() => {
    void preloadStoreManualData(gateway, actor.companyId);
    void preloadStaffRolesData(gateway, actor.companyId);
    void preloadNotificationsData(gateway, actor.companyId);
    void preloadBulletinsData(gateway, actor.companyId);
    void preloadShiftHandoversData(gateway, actor.companyId);
    void preloadTemplatesData(gateway, actor.companyId);
    void preloadTasksListView(gateway, actor, 'board', supervisedIds);
    void preloadTasksListView(gateway, actor, 'created', supervisedIds);
    void preloadTasksListView(gateway, actor, 'overdue', supervisedIds);
    void preloadTasksListView(gateway, actor, 'personal', supervisedIds);
  }, 350);

  // المرحلة الثالثة (أثناء خمول أعمق): التقارير، الوسائط، التصنيفات، المستخدمين، الأدوار، والإشراف
  runWhenIdle(() => {
    void preloadReportsData(gateway, actor.companyId, timeZone, lookups?.memberNames);
    void preloadCategoriesData(gateway, actor.companyId);
    void preloadMediaHubData(gateway, actor.companyId);
    void preloadUsersDirectoryData(gateway, actor.companyId);
    void preloadRolesData(gateway, actor.companyId);
    void preloadSupervisionData(gateway, actor.companyId);
  }, 300);
}

/**
 * تحميل فوري مسبق لبيانات المسار المحدد بمجرد تحريك المؤشر أو اللمس (Hover / Touch Prefetch)
 */
export function prefetchRouteData(
  route: string,
  gateway: any,
  actor: any,
  timeZone = 'Asia/Riyadh',
  memberNames: Map<string, string> = new Map(),
  supervisedIds: string[] = [],
) {
  if (!actor?.companyId) return;
  const companyId = actor.companyId;

  if (route === '/' || route === '/dashboard') {
    void preloadDashboardData(gateway, actor, timeZone, supervisedIds);
  } else if (route.startsWith('/tasks')) {
    void preloadTasksListView(gateway, actor, 'board', supervisedIds);
    void preloadTasksListView(gateway, actor, 'assigned', supervisedIds);
  } else if (route.startsWith('/manual')) {
    void preloadStoreManualData(gateway, companyId);
  } else if (route.startsWith('/staff-roles')) {
    void preloadStaffRolesData(gateway, companyId);
  } else if (route.startsWith('/bulletins')) {
    void preloadBulletinsData(gateway, companyId);
  } else if (route.startsWith('/shifts')) {
    void preloadShiftHandoversData(gateway, companyId);
  } else if (route.startsWith('/media')) {
    void preloadMediaHubData(gateway, companyId);
  } else if (route.startsWith('/templates')) {
    void preloadTemplatesData(gateway, companyId);
  } else if (route.startsWith('/reports')) {
    void preloadReportsData(gateway, companyId, timeZone, memberNames);
  } else if (route.startsWith('/notifications')) {
    void preloadNotificationsData(gateway, companyId);
  } else if (route.startsWith('/admin/categories')) {
    void preloadCategoriesData(gateway, companyId);
  } else if (route.startsWith('/admin/users')) {
    void preloadUsersDirectoryData(gateway, companyId);
  } else if (route.startsWith('/admin/roles')) {
    void preloadRolesData(gateway, companyId);
  } else if (route.startsWith('/admin/supervision')) {
    void preloadSupervisionData(gateway, companyId);
  } else if (route.startsWith('/leaderboard')) {
    void preloadReportsData(gateway, companyId, timeZone, memberNames);
    void preloadDashboardData(gateway, actor, timeZone, supervisedIds);
  } else if (route.startsWith('/about') || route.startsWith('/platform/about')) {
    // Instant static about page
  } else if (route.startsWith('/assets') || route.startsWith('/automations') || route.startsWith('/onboarding')) {
    void preloadBulletinsData(gateway, companyId);
  }
}
